<aran-page-title
  [headerConfig]="{
    bannerTitle: appInfo.appName + ' | ' + routeMetaData.accessAndUse.title
  }"
></aran-page-title>

<div class="api-instruction-container">
  <div class="api-instruction-container__row">
    <h3>Introduction</h3>
    <p>
      Welcome to the API Instruction page. Below you will find detailed
      information about the SWIM APIs supported by our system, their interfaces,
      URLs, authentication mechanisms, and how to manage your credentials.
    </p>
    <mat-divider class="divider"></mat-divider>
    <div>
      <aran-tabs
        *ngIf="!(isLoading$ | async)"
        [tabs]="accessAndUseTabs"
        [selectedIndex]="selectedIndex"
        (tabChanged)="onTabChanged($event)"
        [design]="'underline'"
        [fitInkBarToContent]="true"
        [stretchTabs]="false"
        [alignTabs]="'start'"
        [dynamicHeight]="false"
        [animationDuration]="'300ms'"
        [ignoreContentAnimation]="true"
      >
      </aran-tabs>
      <ng-template #authAndAccessTemplate>
        <div class="my-8">
          <h3>Authentication and Access</h3>
          <h4>Overview</h4>
          <p>
            The Aeronautical Information Request Service uses a standardized
            authentication and access control mechanism for its Data Retrieval
            API. This ensures consistent security and simplifies credential
            management.
          </p>

          <h4>Server Authentication</h4>
          <p>
            The API uses an X.509 certificate to establish a secure HTTPS
            connection, ensuring the server’s identity is verified.
          </p>
          <h4>Client Authentication</h4>
          <p>Clients authenticate using HTTP Basic Authentication:</p>
          <div>
            <ul>
              <li>
                <h4>Credentials:</h4>
                <ul>
                  <li>
                    <strong>Username:</strong> Email address registered in the
                    Client Portal
                  </li>
                  <li>
                    <strong>Password:</strong> API password generated via the
                    Client Portal
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div>
            <h4><strong>Access Control</strong></h4>
            <ul>
              <li>
                Access to API is controlled through permissions configured in
                the Client Portal.
              </li>
              <li>
                Clients can only interact with services they are explicitly
                authorized for.
              </li>
              <li>
                If you don’t have a password, or if you’ve forgotten it, click
                the button below to generate/reset your API password.
              </li>
              <li>
                Passwords are specific to the API and do not affect your portal
                login.
              </li>
            </ul>
          </div>

          <div class="mt-8 flex items-center">
            <aran-button
              *ngIf="!(isLoading$ | async)"
              [type]="'button'"
              [icon]="'lock_reset'"
              [name]="
                isExistApi ? 'Reset API Password' : 'Generate API Password'
              "
              [disabled]="false"
              (handleClick)="generate()"
            ></aran-button>
          </div>

          <ng-container>
            <ng-template #setApiPassword>
              <app-generate-api-password></app-generate-api-password>
            </ng-template>

            <aran-drawer-modal
              [isDrawerOpen]="false"
              [templateRef]="currentTemplate"
              [modalTitle]="modalTitle"
            ></aran-drawer-modal>
          </ng-container>
        </div>
      </ng-template>
      <ng-template #aeronauticalInformationTemplate>
        <div class="my-8">
          <h3>Aeronautical Information Request Service</h3>
          <p>
            The Aeronautical Information Request Service allows the service
            consumer to get aeronautical information concerning the actual and
            future infrastructure to be used by air traffic such as aerodromes,
            airspaces, navigation aids and ATS routes.
          </p>
          <p>
            The information returned is in the form of an AIXM 5.1.1 message.
          </p>
          <p>
            This document contains information about the Aeronautical
            Information Request Service and its single API:
          </p>
          <p>
            <strong>Data Retrieval API </strong> – Allows the service consumer
            to request an AIXM Basic Message based on filters.
          </p>
          <h4>Data Retrieval API</h4>
          <p>
            The API enables users to query and retrieve aeronautical event
            information efficiently using standardized filters and protocols. It
            follows OGC Web Feature Service (WFS) standards and supports
            retrieval of AIXM 5.1.1 features through the GetFeature operation.
          </p>

          <div class="my-6">
            <i
              ><h4>
                {{
                  accessAndUseData?.aeronauticalInformationInfo
                    ?.dataRetrievalInfo
                }}
              </h4></i
            >
          </div>

          <p>
            Detailed information about supported query filters, available
            features, and service conformance is provided in the GetCapabilities
            document, which will be attached alongside this specification. This
            document adheres to established standards: filters are defined by
            the Filter Encoding Standard Temporality Extension (FES-TE),
            features are described according to the WFS specification, and
            supported service conformances are declared according to the WFS
            Temporality Extension (WFS-TE). Reviewing the GetCapabilities
            document will help users understand how to construct valid queries
            and what to expect from the API.
          </p>
          <h4>Specifications</h4>
          <p>
            This service follows standard practices to ensure reliable and
            efficient communication. Key details include:
          </p>
          <ul>
            <li>
              Communication takes place over HTTPS, using the HTTP protocol.
            </li>
            <li>Data is encoded in <strong>XML</strong> format.</li>
            <li>
              The service uses a web service interface based on the
              <strong>SWIM_TI_YP_1_0_WS_LIGHT</strong> standard.
            </li>
            <li>
              Communication follows a synchronous request and reply pattern,
              meaning you send a request and get a response right away.
            </li>
            <li>
              Optional <strong>gzip</strong> compression can be used to speed up
              data transfer if requested by the client.
            </li>
            <li>
              Errors are handled in a standardized way using
              <strong>ExceptionReport</strong>
              compliant with <strong>OGC</strong> specifications to provide
              clear error messages.
            </li>
          </ul>
          <h4>Supported operations</h4>
          <p>
            This service supports a set of standard operations to interact with
            aeronautical data, helping users discover service capabilities and
            retrieve specific information.
          </p>
          <i>GetCapabilities</i>
          <ul>
            <li>
              Description: Provides metadata about the service, including
              available operations, supported formats, and other key information
              to help users understand how to interact with the service.
            </li>
            <li>HTTP Method: POST</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.aeronauticalInformationInfo
                    ?.aeronauticalInformationWfsUrl
                }}/api/aeronautical-data/capabilities</span
              >
            </li>
            <li>
              Purpose: Allows clients to dynamically discover what the service
              offers and how to use it.
            </li>
          </ul>
          <i>GetFeature</i>
          <ul>
            <li>
              Description: Retrieves a selection of AIXM 5.1.1 features from the
              data store based on the query criteria specified in the request.
              Returns an AIXM Basic Message containing the matching data.
            </li>
            <li>HTTP Method: POST</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.aeronauticalInformationInfo
                    ?.aeronauticalInformationWfsUrl
                }}/api/aeronautical-data</span
              >
            </li>
            <li>
              Purpose: Enables clients to request specific aeronautical features
              filtered by their query. 
            </li>
          </ul>
        </div>
      </ng-template>
      <ng-template #digitalNotamTemplate>
        <div class="my-8">
          <h3>Digital Notam Subscription and Request Service</h3>
          <p>
            The Digital NOTAM Subscription and Request Service allows the
            service consumer to get aeronuatical information in accordance with
            the Digital NOTAM specification. The aeronautical information
            conforms to the event scenarios that are supported by Digital NOTAM
            such as runway closures.
          </p>
          <p>
            The service consumer may subscribe to the service, specifying the
            event scenarios of interest. It is also possible to send a direct
            request to the service to get the aeronautical information.
          </p>
          <p>
            The information returned is in the form of an AIXM 5.1.1 message.
          </p>
          <p>The document contains information about three main APIs:</p>
          <ul>
            <li>
              <strong>Data Retrieval API </strong> – Allows the service consumer
              to request an AIXM Basic Message based on filters which are linked
              to the event feature.
            </li>
            <li>
              <strong>Subscription API</strong> – Allows the service consumer to
              select and subscribe to event scenarios of interest. It allows the
              service consumer to manage the subscriptions e.g. to pause a
              subscription of to unsubscribe.
            </li>
            <li>
              <strong>Distribution API (RabbitMQ)</strong> – Allows the service
              provider to distributes AIXM Basic Messages based on the event
              scenario that has triggered the need for the service consumer to
              be updated.
            </li>
          </ul>
          <h4>Data Retrieval API</h4>
          <p>
            The API enables users to query and retrieve aeronautical event
            information efficiently using standardized filters and protocols. It
            follows OGC Web Feature Service (WFS) standards and supports
            retrieval of AIXM 5.1.1 features through the GetFeature operation.
          </p>
          <p>
            Detailed information about supported query filters, available
            features, and service conformance is provided in the GetCapabilities
            document, which will be attached alongside this specification. This
            document adheres to established standards: filters are defined by
            the Filter Encoding Standard Temporality Extension (FES-TE),
            features are described according to the WFS specification, and
            supported service conformances are declared according to the WFS
            Temporality Extension (WFS-TE). Reviewing the GetCapabilities
            document will help users understand how to construct valid queries
            and what to expect from the API.
          </p>
          <h4>Specifications</h4>
          <p>
            This service follows standard practices to ensure reliable and
            efficient communication. Key details include:
          </p>
          <ul>
            <li>
              Communication takes place over HTTPS, using the HTTP protocol.
            </li>
            <li>Data is encoded in <strong>XML</strong> format.</li>
            <li>
              The service uses a web service interface based on the
              <strong>SWIM_TI_YP_1_0_WS_LIGHT</strong> standard.
            </li>
            <li>
              Communication follows a synchronous request and reply pattern,
              meaning you send a request and get a response right away.
            </li>
            <li>
              Optional <strong>gzip</strong> compression can be used to speed up
              data transfer if requested by the client.
            </li>
            <li>
              Errors are handled in a standardized way using ExceptionReport
              compliant with OGC specifications to provide clear error messages.
            </li>
          </ul>
          <h4>Supported operations</h4>
          <p>
            This service supports a set of standard operations to interact with
            aeronautical data, helping users discover service capabilities and
            retrieve specific information.
          </p>
          <i>GetCapabilities</i>
          <ul>
            <li>
              Description: Provides metadata about the service, including
              available operations, supported formats, and other key information
              to help users understand how to interact with the service.
            </li>
            <li>HTTP Method: POST</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo?.digitalNotamWfsUrl
                }}/api/digital-notam/capabilities</span
              >
            </li>
            <li>
              Purpose: Allows clients to dynamically discover what the service
              offers and how to use it.
            </li>
          </ul>
          <i>GetFeature</i>
          <ul>
            <li>
              Description: Retrieves a selection of AIXM 5.1.1 features from the
              data store based on the query criteria specified in the request.
              Returns an AIXM Basic Message containing the matching data.
            </li>
            <li>HTTP Method: POST</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo?.digitalNotamWfsUrl
                }}/api/digital-notam</span
              >
            </li>
            <li>
              Purpose: Enables clients to request specific aeronautical features
              filtered by their query.
            </li>
          </ul>
          <h4>Subscription API</h4>
          <p>
            The API allows clients to create, update, and manage subscriptions
            for digital NOTAMs based on user-defined criteria. Upon successful
            subscription, clients receive the name of a message queue to which
            updates will be published.
          </p>
          <h4>Specifications</h4>
          <p>
            This service follows standard practices to ensure reliable and
            efficient communication. Key details include:
          </p>
          <ul>
            <li>
              Communication takes place over HTTPS, using the HTTP protocol.
            </li>
            <li>Data is encoded in <strong>JSON</strong> format.</li>
            <li>
              The service uses a web service interface based on the
              <strong>SWIM_TI_YP_1_0_WS_LIGHT</strong> standard.
            </li>
            <li>
              Communication follows a synchronous request and reply pattern,
              meaning you send a request and get a response right away.
            </li>
          </ul>
          <h4>Supported operations</h4>
          <p>
            These operations are defined by the SWIM Digital NOTAM Subscription
            Interface and implemented in our API to manage event scenario
            subscriptions.
          </p>
          <i>Subscribe</i>
          <ul>
            <li>
              <p>
                Description: Allows a service consumer to subscribe to event
                scenarios of interest.
              </p>
              <p>
                Note: It is not possible to create more than one subscription
                for the same event scenario.
              </p>
            </li>
            <li>HTTP Method: POST</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions</span
              >
            </li>
            <li>
              Purpose: Enables clients to create a new subscription to receive
              notifications for specified event scenarios.
            </li>
          </ul>
          <i>Unsubscribe</i>
          <ul>
            <li>
              Description: Allows a service consumer to unsubscribe from event
              scenarios of interest.
            </li>
            <li>HTTP Method: DELETE</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions</span
              >
            </li>
            <li>
              Purpose: Enables clients to delete an existing subscription and
              stop receiving notifications.
            </li>
          </ul>
          <i>Pause</i>
          <ul>
            <li>
              Description: Allows a service consumer to pause the subscription
              for a particular topic subscription.
            </li>
            <li>HTTP Method: PUT</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions/pause/&#123;subscriptionId&#125;</span
              >
            </li>
            <li>
              Purpose: Temporarily suspends notifications for a given
              subscription without deleting it.
            </li>
          </ul>
          <i>Resume</i>
          <ul>
            <li>
              Description: Allows a service consumer to resume a subscription
              that had been paused.
            </li>
            <li>HTTP Method: PUT</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions/resume/&#123;subscriptionId&#125;</span
              >
            </li>
            <li>
              Purpose: Reactivates a paused subscription to start receiving
              notifications again.
            </li>
          </ul>
          <i>GetSubscriptions</i>
          <ul>
            <li>
              Description: Allows a service consumer to obtain the list of
              subscriptions.
            </li>
            <li>HTTP Method: GET</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions</span
              >
            </li>
            <li>
              Purpose: Provides all subscriptions that belong to the requesting
              client.
            </li>
          </ul>
          <i>GetSubscriptionDetails</i>
          <ul>
            <li>
              Description: Allows a service consumer to obtain information on a
              specific subscription.
            </li>
            <li>HTTP Method: GET</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/subscriptions/&#123;subscriptionId&#125;</span
              >
            </li>
            <li>
              Purpose: Provides detailed data about a particular subscription.
            </li>
          </ul>
          <i>GetTopics</i>
          <ul>
            <li>
              Description: Allows a service consumer to request the list of
              topics available for subscription.
            </li>
            <li>HTTP Method: GET</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/topics</span
              >
            </li>
            <li>
              Purpose: Enables clients to discover available event topics for
              subscription.
            </li>
          </ul>
          <i>GetTopic</i>
          <ul>
            <li>
              Description: Allows a service consumer to obtain information on a
              specific topic.
            </li>
            <li>HTTP Method: GET</li>
            <li>
              Endpoint:
              <span class="accessanduseLink"
                >{{
                  accessAndUseData?.digitalNotamInfo
                    ?.digitalNotamDistributorSubscriptionUrl
                }}/api/topics/&#123;topicId&#125;</span
              >
            </li>
            <li>
              Purpose: Provides detailed data about a particular event topic.
            </li>
          </ul>
          <h4>Topic Naming Pattern</h4>
          <p>
            When subscribing to digital NOTAM updates, clients need to specify a
            topic name that tells the system what kind of events they’re
            interested in and for which area. The topic follows a simple,
            consistent structure:
          </p>
          <i>Format</i>
          <p>DNSRS/&lt;ScenarioName&gt;/&lt;AreaOfInterest&gt;</p>
          <i>What each part means</i>
          <ul>
            <li>
              DNSRS This prefix stands for "Digital NOTAM Subscription and
              Request Service" and shows that the topic is related to digital
              NOTAM updates.
            </li>
            <li>
              Scenario Name This defines the type of event you want to subscribe
              to. For example: AD.CLS → Events related to aerodrome closures
            </li>
            <li>
              Area of Interest This is the specific location you want updates
              for —an airport or FIR (Flight Information Region). For example:
              {{ accessAndUseData?.digitalNotamInfo?.sampleAirport }} → ICAO
              code for a particular airport
            </li>
          </ul>
          <i>Wildcards in Subscriptions</i>
          <p>
            To make subscriptions more flexible, the following wildcards can be
            used:
          </p>
          <p>* (asterisk)</p>
          <p>Matches one level in the topic hierarchy.</p>
          <p>
            Example: DNSRS/AD.CLS/* will match all aerodrome closures for any
            location.
          </p>
          <p># (hash)</p>
          <p>Matches all levels from that point onward.</p>
          <p>
            Example: DNSRS/# will match all types of digital NOTAM updates, for
            any scenario and any location you are authorized to receive.
          </p>
          <i>Examples</i>
          <ul>
            <li>
              DNSRS/AD.CLS/{{
                accessAndUseData?.digitalNotamInfo?.sampleAirport
              }}
              Subscribe to aerodrome closure updates for the
              {{ accessAndUseData?.digitalNotamInfo?.sampleAirport }} airport.
            </li>
            <li>
              DNSRS/AD.CLS/* Subscribe to all aerodrome closure updates, for
              every location you have access to.
            </li>
            <li>
              DNSRS/# Subscribe to all digital NOTAM updates for all scenarios
              and areas you are authorized to receive.
            </li>
          </ul>
          <h3>Distribution API – RabbitMQ</h3>
          <p>
            The Distribution API supports the real-time distribution of digital
            NOTAM updates via message queues. After subscribing through the
            Subscription API, clients receive a dedicated RabbitMQ queue to
            which relevant event data is published as it occurs. This allows
            clients to listen for real-time updates in the form of AIXM Basic
            Messages.
          </p>
          <h4>Specifications</h4>
          <p>
            This service follows standard practices to ensure reliable and
            efficient communication. Key details include:
          </p>
          <ul>
            <li>
              Communication takes place over AMQPs, using the AMQP protocol.
            </li>
            <li>
              The interface follows the
              <strong>SWIM_TI_YP_1_1_AMQP_MESSAGING</strong> standard.
            </li>
            <li>
              Communication follows a publish-subscribe pattern, where updates
              are sent to subscribed clients as they happen.
            </li>
            <li>
              Published data is formatted as
              <strong>AIXM 5.1.1</strong> messages.
            </li>
            <li>
              Clients must connect to their assigned queue to receive
              notifications.
            </li>
          </ul>
          <h4>Client Integration Details</h4>
          <p>
            To receive real-time Digital NOTAM updates, clients must connect to
            the RabbitMQ queue provided during subscription. Use the following
            URI to establish a secure connection:
          </p>
          <ul>
            <li>
              Host URL:
              <span class="accessanduseLink">{{
                accessAndUseData?.digitalNotamInfo?.digitalNotamRabbitMqUrl
              }}</span>
            </li>
          </ul>
          <h3>Subscription Flow</h3>
          <h4>How to Subscribe and Receive Updates</h4>
          <p><strong>1. Call the Subscription API</strong></p>
          <p>
            Send a subscription request with the topic you want, for example:
            DNSRS/AD.CLS/{{
              accessAndUseData?.digitalNotamInfo?.sampleAirport
            }}.
          </p>
          <p><strong>2. Get Your Queue Name</strong></p>
          <p>
            If you have permission to receive updates for that topic, the API
            will reply with a unique RabbitMQ queue name just for you.
          </p>
          <p><strong>3. Connect to the Queue</strong></p>
          <p>
            Use the Distribution API to connect to that queue and start
            receiving real-time updates. These updates come as AIXM Basic
            Messages.
          </p>
        </div>
        <h4>Access Control</h4>
        <p>
          You will only get a queue name if you are authorized to receive
          notifications for the chosen event scenario and area.
        </p>
        <p>
          Additionally, you will be able to connect to the queue only if you
          have an active subscription for it.
        </p>
      </ng-template>
    </div>
  </div>
</div>
